import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => (name.trim().charAt(0) || '?').toUpperCase()
const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const money    = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'

const STATUS_MAP = {
  1: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active'   },
  0: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Inactive' },
}
const sc = (s) => STATUS_MAP[s] ?? STATUS_MAP[Number(s)] ?? { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: '—' }

const ORDER_STATUS_MAP = {
  pending:    { bg: '#fffbeb', color: '#b45309' },
  assigned:   { bg: '#eff6ff', color: '#1d4ed8' },
  picked_up:  { bg: '#f5f3ff', color: '#7c3aed' },
  delivered:  { bg: '#f0fdf4', color: '#15803d' },
  cancelled:  { bg: '#fef2f2', color: '#b91c1c' },
  returned:   { bg: '#fff7ed', color: '#c2410c' },
}
const oc = (s) => ORDER_STATUS_MAP[s?.toLowerCase()] ?? { bg: '#f3f4f6', color: '#6b7280' }

const stars = (rating, max = 5) => {
  const r = Math.round(rating * 2) / 2
  return Array.from({ length: max }, (_, i) => {
    if (i + 1 <= r) return '★'
    if (i + 0.5 === r) return '½'
    return '☆'
  }).join('')
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const SS = {
  select: { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" },
  input:  { width: '100%', height: 42, borderRadius: 8, border: '1px solid #e9e9e4', padding: '0 13px', fontSize: 13, fontWeight: 500, color: '#111317', outline: 'none', background: '#fff', fontFamily: "'Plus Jakarta Sans',sans-serif", boxSizing: 'border-box' },
  label:  { display: 'block', fontSize: 11, fontWeight: 700, color: '#111317', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: "'JetBrains Mono',monospace" },
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function Badge({ status }) {
  const s = sc(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

// ── Register Driver Modal ─────────────────────────────────────────────────────
function RegisterModal({ companies, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', vehicle_type: '', vehicle_plate: '', vehicle_company: '', status: 1 })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      const res = await api.post('/driver/admin/drivers', {
        name: form.name,
        phone: form.phone,
        vehicle_type: form.vehicle_type,
        vehicle_company: form.vehicle_company,
        plate_number: form.vehicle_plate,
        status: form.status,
      })
      onSaved(res.data.driver)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to register driver')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 500, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>

        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Register New Driver</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><label style={SS.label}>Full Name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="John Doe" style={SS.input} /></div>
              <div><label style={SS.label}>Phone *</label><input required value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="078XXXXXXX" style={SS.input} /></div>

              <div><label style={SS.label}>Vehicle Type</label><input value={form.vehicle_type} onChange={e => set('vehicle_type', e.target.value)} placeholder="Motorcycle / Car / Van" style={SS.input} /></div>
              <div><label style={SS.label}>Vehicle Plate</label><input value={form.vehicle_plate} onChange={e => set('vehicle_plate', e.target.value)} placeholder="RAB 123A" style={SS.input} /></div>
              <div>
                <label style={SS.label}>Company</label>
                <input value={form.vehicle_company} onChange={e => set('vehicle_company', e.target.value)} placeholder="Company name or leave blank" style={SS.input} />
              </div>
            </div>
            <div>
              <label style={SS.label}>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} style={{ ...SS.input, cursor: 'pointer' }}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
            {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '10px 14px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>}
          </div>
          <div style={{ padding: '14px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Registering…' : 'Register Driver'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Register Company Modal ────────────────────────────────────────────────────
// Companies are auto-created from vehicle_company field on drivers
// No separate company creation — just name drivers with a company and it appears here
function CompanyModal({ onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>About Companies</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>
        <div style={{ padding: '24px', fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
          <p>Companies are automatically created when you register a driver and assign them a <b>Company Name</b>.</p>
          <p style={{ marginTop: 10 }}>All drivers sharing the same company name are grouped together in the Companies tab.</p>
          <p style={{ marginTop: 10 }}>To add a company, register a driver and fill in the <b>Company</b> field.</p>
        </div>
        <div style={{ padding: '0 24px 20px' }}>
          <button onClick={onClose} style={{ width: '100%', height: 42, borderRadius: 8, border: 'none', background: '#111317', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Got it</button>
        </div>
      </div>
    </>
  )
}

// ── Edit Driver Modal ─────────────────────────────────────────────────────────
function EditDriverModal({ driver, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:            driver.name            || '',
    phone:           driver.phone           || '',
    vehicle_type:    driver.vehicle_type    || '',
    vehicle_company: driver.company_name    || '',
    plate_number:    driver.vehicle_plate   || '',
  })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      const res = await api.put(`/driver/admin/drivers/${driver.driver_id}`, form)
      onSaved(res.data.driver)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update driver')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 480, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>

        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Edit Driver</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2, fontFamily: "'JetBrains Mono',monospace" }}>#{driver.driver_id} · {driver.phone}</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={SS.label}>Full Name *</label>
                <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="John Doe" style={SS.input} />
              </div>
              <div>
                <label style={SS.label}>Phone *</label>
                <input required value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="078XXXXXXX" style={SS.input} />
              </div>
              <div>
                <label style={SS.label}>Vehicle Type</label>
                <input value={form.vehicle_type} onChange={e => set('vehicle_type', e.target.value)} placeholder="Motorcycle / Car / Van" style={SS.input} />
              </div>
              <div>
                <label style={SS.label}>Vehicle Plate</label>
                <input value={form.plate_number} onChange={e => set('plate_number', e.target.value)} placeholder="RAB 123A" style={SS.input} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={SS.label}>Company</label>
                <input value={form.vehicle_company} onChange={e => set('vehicle_company', e.target.value)} placeholder="Company name or leave blank" style={SS.input} />
              </div>
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '10px 14px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                ⚠ {error}
              </div>
            )}
          </div>

          <div style={{ padding: '14px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Driver Detail Drawer ──────────────────────────────────────────────────────
function DriverDrawer({ driver, onClose, onRemove, onStatusChange, onEdit }) {
  const [tab,        setTab]        = useState('profile')
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)

  // Fetch full driver detail (orders + reviews) when drawer opens
  useEffect(() => {
    if (!driver) return
    setTab('profile')
    setDetail(null)
    setDetailLoad(true)
    api.get(`/driver/admin/drivers/${driver.driver_id}`)
      .then(res => setDetail(res.data.driver))
      .catch(() => setDetail(driver))   // fallback to list row data
      .finally(() => setDetailLoad(false))
  }, [driver?.driver_id])

  if (!driver) return null

  // Use full detail if loaded, otherwise fall back to list row
  const d   = detail || driver
  const bg  = avatarBg(d.name || '')
  const avg = d.avg_rating ? parseFloat(d.avg_rating).toFixed(1) : null

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }`}</style>

        {/* Header — always uses live driver prop for instant display */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#111317', flexShrink: 0 }}>{initials(d.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{d.name}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{d.phone} · {d.vehicle_type || 'Vehicle not set'}</div>
          </div>
          <Badge status={d.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 8 }}>×</div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 24px' }}>
          {[['profile','Profile'],['orders','Orders'],['reviews','Reviews']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: '11px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1 }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* Loading spinner while fetching detail */}
          {detailLoad && (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>
          )}

          {/* ── Profile Tab ── */}
          {!detailLoad && tab === 'profile' && (
            <>
              {avg && (
                <div style={{ background: '#f9f9f7', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: '#111317', lineHeight: 1 }}>{avg}</div>
                    <div style={{ fontSize: 14, color: '#f59e0b', marginTop: 2 }}>{stars(parseFloat(avg))}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{d.review_count || 0} reviews</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {[5,4,3,2,1].map(s => {
                      const count = d.rating_breakdown?.[s] || 0
                      const total = d.review_count || 1
                      const pct   = Math.round((count / total) * 100)
                      return (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, color: '#9a9a94', width: 8 }}>{s}</span>
                          <div style={{ flex: 1, height: 5, background: '#e9e9e4', borderRadius: 3 }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: '#f59e0b', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", width: 24 }}>{count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {[
                ['Driver ID',        d.driver_id],
                ['Full Name',        d.name],
                ['Phone',            d.phone],
                ['Vehicle Type',     d.vehicle_type   || '—'],
                ['Vehicle Plate',    d.vehicle_plate  || '—'],
                ['Company',          d.company_name   || 'Unaffiliated'],
                ['Total Deliveries', d.total_deliveries     ?? '—'],
                ['Completed',        d.completed_deliveries ?? '—'],
                ['Cancelled',        d.cancelled_deliveries ?? '—'],
                ['Registered',       fmt(d.created_at)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{v}</span>
                </div>
              ))}
            </>
          )}

          {/* ── Orders Tab ── */}
          {!detailLoad && tab === 'orders' && (
            !d.orders?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No orders assigned yet.</div>
              : d.orders.map((o, i) => (
                  <div key={o.order_id || i} style={{ background: '#f9f9f7', borderRadius: 10, padding: '14px 16px', marginBottom: 10, border: '1px solid #e9e9e4' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>Order #{o.order_id}</div>
                      <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, background: oc(o.status).bg, color: oc(o.status).color, textTransform: 'uppercase' }}>{o.status}</span>
                    </div>
                    {[
                      ['Customer', o.customer_name || o.customer_phone || '—'],
                      ['Pickup',   o.pickup_address   || '—'],
                      ['Delivery', o.delivery_address || '—'],
                      ['Amount',   money(o.total_price)],
                      ['Date',     fmt(o.created_at)],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 12, marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: '#9a9a94', width: 60, flexShrink: 0 }}>{k}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#111317' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                ))
          )}

          {/* ── Reviews Tab ── */}
          {!detailLoad && tab === 'reviews' && (
            !d.reviews?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No reviews yet.</div>
              : d.reviews.map((r, i) => (
                  <div key={r.review_id || i} style={{ background: '#f9f9f7', borderRadius: 10, padding: '14px 16px', marginBottom: 10, border: '1px solid #e9e9e4' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: avatarBg(r.reviewer_name || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{initials(r.reviewer_name)}</div>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{r.reviewer_name || 'Anonymous'}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: 13, color: '#f59e0b' }}>{stars(r.rating)}</span>
                        <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginLeft: 6 }}>{r.rating}/5</span>
                      </div>
                    </div>
                    {r.comment && <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 4 }}>{r.comment}</div>}
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(r.created_at)}</div>
                  </div>
                ))
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 8 }}>
          <button onClick={() => onEdit(d)}
            style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #e9e9e4', cursor: 'pointer', background: '#fff', color: '#111317', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Edit
          </button>
          <button onClick={() => onStatusChange(d, d.status)}
            style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: d.status === 1 ? '#fef2f2' : '#f0fdf4', color: d.status === 1 ? '#b91c1c' : '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {d.status === 1 ? 'Deactivate' : 'Activate'}
          </button>
          <button onClick={() => onRemove(d)}
            style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #fecaca', cursor: 'pointer', background: '#fff5f5', color: '#b91c1c', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Remove
          </button>
        </div>
      </div>
    </>
  )
}

// ── Remove Confirm Modal ──────────────────────────────────────────────────────
function RemoveModal({ driver, onClose, onConfirm, loading }) {
  if (!driver) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Remove Driver?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            <b>{driver.name}</b> will be permanently removed from the platform. Assigned orders will need to be reassigned.
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Removing…' : 'Yes, Remove'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Drivers() {
  const [tab,          setTab]          = useState('drivers')    // 'drivers' | 'companies'
  const [drivers,      setDrivers]      = useState([])
  const [companies,    setCompanies]    = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [stats,        setStats]        = useState({ total: 0, active: 0, inactive: 0, pending: 0 })
  const [selected,     setSelected]     = useState(null)
  const [removing,     setRemoving]     = useState(null)
  const [removeLoad,   setRemoveLoad]   = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [showCompany,  setShowCompany]  = useState(false)
  const [editing,      setEditing]      = useState(null)

  const PER_PAGE = 15

  // ── Fetch drivers ──
  const fetchDrivers = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search        && { search }),
        ...(statusFilter  && { status: statusFilter }),
      })
      const res = await api.get(`/driver/admin/drivers?${params}`)
      setDrivers(res.data.drivers    || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(res.data.stats        || { total: 0, active: 0, inactive: 0, pending: 0 })
    } catch {
      setError('Failed to load drivers')
      // Mock
      const mock = Array.from({ length: 10 }, (_, i) => ({
        driver_id: i + 1, name: `Driver ${i + 1}`, phone: `078${String(i+1).padStart(7,'0')}`,
        license_number: `DL-00${i+1}`, vehicle_type: ['Motorcycle','Car','Van'][i%3],
        vehicle_plate: `RAB ${i+1}00A`, company_id: i % 3 === 0 ? null : Math.ceil((i+1)/3),
        company_name: i % 3 === 0 ? null : `Company ${Math.ceil((i+1)/3)}`,
        status: [1, 1, 0, 0, 1][i%5],
        avg_rating: (3.5 + Math.random() * 1.5).toFixed(1),
        review_count: Math.floor(Math.random() * 40),
        total_deliveries: Math.floor(Math.random() * 200),
        completed_deliveries: Math.floor(Math.random() * 180),
        cancelled_deliveries: Math.floor(Math.random() * 20),
        created_at: new Date(Date.now() - i * 86400000 * 30).toISOString(),
        orders: Array.from({ length: 3 }, (_, j) => ({
          order_id: j + 1 + i * 3, status: ['delivered','assigned','picked_up'][j],
          customer_name: `Customer ${j+1}`, total_price: 25 + j * 15,
          pickup_address: 'KG 7 Ave, Kigali', delivery_address: 'KN 3 Rd, Kigali',
          created_at: new Date(Date.now() - j * 86400000).toISOString(),
        })),
        reviews: Array.from({ length: 2 }, (_, j) => ({
          review_id: j + 1, rating: 4 + j % 2, reviewer_name: `User ${j+1}`,
          comment: j === 0 ? 'Very fast and professional delivery!' : 'Good service overall.',
          created_at: new Date(Date.now() - j * 86400000 * 3).toISOString(),
        })),
        rating_breakdown: { 5: 12, 4: 8, 3: 3, 2: 1, 1: 0 },
      }))
      setDrivers(mock)
      setTotalPages(2); setTotalCount(20)
      setStats({ total: 20, active: 14, inactive: 6 })
    } finally { setLoading(false) }
  }, [page, search, statusFilter])

  // ── Fetch companies ──
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/driver/admin/companies')
      setCompanies(res.data.companies || [])
    } catch {
      setCompanies(Array.from({ length: 4 }, (_, i) => ({
        company_id: i + 1, company_name: `Company ${i + 1}`,
        phone: `078${String(i+1).padStart(7,'0')}`, email: `co${i+1}@example.com`,
        address: 'Kigali, Rwanda', registration_number: `RCA-00${i+1}`,
        driver_count: Math.floor(Math.random() * 10) + 1,
        created_at: new Date(Date.now() - i * 86400000 * 60).toISOString(),
      })))
    }
  }, [])

  useEffect(() => { fetchDrivers(); fetchCompanies() }, [fetchDrivers, fetchCompanies])
  useEffect(() => { setPage(1) }, [search, statusFilter])

  const handleStatusChange = async (driver, newStatus) => {
    try {
      await api.patch(`/driver/admin/drivers/${driver.driver_id}/status`, { status: newStatus === 1 ? 0 : 1 })
      setDrivers(prev => prev.map(d => d.driver_id === driver.driver_id ? { ...d, status: newStatus } : d))
      if (selected?.driver_id === driver.driver_id) setSelected(prev => ({ ...prev, status: newStatus }))
    } catch { alert('Failed to update driver status') }
  }

  const handleRemove = async () => {
    if (!removing) return
    setRemoveLoad(true)
    try {
      await api.delete(`/driver/admin/drivers/${removing.driver_id}`)
      setDrivers(prev => prev.filter(d => d.driver_id !== removing.driver_id))
      setRemoving(null); setSelected(null)
    } catch (err) { alert(err.response?.data?.error || 'Failed to remove driver') }
    finally { setRemoveLoad(false) }
  }

  const handleDriverSaved = (updatedDriver) => {
    setDrivers(prev => prev.map(d =>
      d.driver_id === updatedDriver.driver_id ? { ...d, ...updatedDriver } : d
    ))
    if (selected?.driver_id === updatedDriver.driver_id) {
      setSelected(prev => ({ ...prev, ...updatedDriver }))
    }
  }

  const handleRemoveCompany = async (company) => {
    if (!window.confirm(`Remove company "${company.company_name}"? Their drivers will become unaffiliated.`)) return
    try {
      await api.delete(`/driver/admin/companies/${encodeURIComponent(company.company_name)}`)
      setCompanies(prev => prev.filter(c => c.company_name !== company.company_name))
      fetchCompanies()
    } catch (err) { alert(err.response?.data?.error || 'Failed to remove company') }
  }

  const filtered = drivers.filter(d =>
    !search || [d.name, d.phone, d.license_number, d.vehicle_plate, d.company_name]
      .some(f => f?.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Drivers & Companies</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Manage delivery drivers, logistics companies and their orders.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowCompany(true)} style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>+ Add Company</button>
          <button onClick={() => setShowRegister(true)} style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 2px 8px rgba(232,68,10,0.25)' }}>+ Register Driver</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Drivers" value={stats.total.toLocaleString()}   />
        <StatCard label="Active"    value={stats.active.toLocaleString()}   accent="#15803d" />
        <StatCard label="Inactive"  value={stats.inactive.toLocaleString()} accent="#b91c1c" />
        <StatCard label="Companies"     value={companies.length.toLocaleString()} accent="#1d4ed8" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 6, width: 'fit-content' }}>
        {[['drivers','🚚 Drivers'],['companies','🏢 Companies']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: tab === id ? '#111317' : 'transparent', color: tab === id ? '#fff' : '#9a9a94', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.15s' }}>{label}</button>
        ))}
      </div>

      {/* ── DRIVERS TAB ── */}
      {tab === 'drivers' && (
        <>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
              <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, license or plate…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
              {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={SS.select}>
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <button onClick={fetchDrivers} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalCount.toLocaleString()} drivers</div>
          </div>

          {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error} — showing mock data</div>}

          {/* Table */}
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1.2fr 1fr 0.8fr 0.8fr 0.9fr 0.8fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
              {['Driver','Phone','License','Company','Vehicle','Rating','Status','Actions'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>

            {loading ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading drivers…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No drivers found.</div>
            ) : (
              filtered.map((d, i) => {
                const bg = avatarBg(d.name || '')
                return (
                  <div key={d.driver_id} onClick={() => setSelected(d)}
                    style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1.2fr 1fr 0.8fr 0.8fr 0.9fr 0.8fr', padding: '13px 20px', borderBottom: i < filtered.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.12s', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Driver */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials(d.name)}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{d.driver_id}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace" }}>{d.phone}</div>
                    <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", color: '#9a9a94' }}>{d.license_number || '—'}</div>
                    <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.company_name || <span style={{ color: '#9a9a94', fontStyle: 'italic' }}>Unaffiliated</span>}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94' }}>{d.vehicle_type || '—'}<br /><span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10 }}>{d.vehicle_plate || ''}</span></div>
                    <div>
                      {d.avg_rating ? (
                        <div>
                          <span style={{ fontSize: 12, color: '#f59e0b' }}>★</span>
                          <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 3 }}>{parseFloat(d.avg_rating).toFixed(1)}</span>
                          <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{d.review_count || 0} reviews</div>
                        </div>
                      ) : <span style={{ fontSize: 11, color: '#9a9a94' }}>No ratings</span>}
                    </div>
                    <div><Badge status={d.status} /></div>
                    <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => setSelected(d)} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                      <button onClick={() => { setRemoving(d) }} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Remove</button>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
                  return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
                })}
                <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── COMPANIES TAB ── */}
      {tab === 'companies' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {companies.length === 0
            ? <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '48px 0' }}>No companies registered yet.</div>
            : companies.map(c => (
                <div key={c.company_id} style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 9, background: avatarBg(c.company_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800 }}>{initials(c.company_name)}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 800 }}>{c.company_name}</div>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{c.company_id}</div>
                      </div>
                    </div>
                    <button onClick={() => handleRemoveCompany(c)} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Remove</button>
                  </div>
                  {[
                    ['Total Drivers',  `${c.driver_count   || 0}`],
                    ['Active Drivers', `${c.active_drivers || 0}`],
                    ['Avg Rating',     c.avg_rating ? `${parseFloat(c.avg_rating).toFixed(1)} ★` : 'No ratings'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f3f3f0' }}>
                      <span style={{ fontSize: 11, color: '#9a9a94' }}>{k}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace', color: '#111317", maxWidth: 180, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
                    </div>
                  ))}
                </div>
              ))
          }
        </div>
      )}

      {/* Drawers & Modals */}
      <DriverDrawer
        driver={selected}
        onClose={() => setSelected(null)}
        onRemove={d => { setRemoving(d); setSelected(null) }}
        onStatusChange={handleStatusChange}
        onEdit={d => { setEditing(d); setSelected(null) }}
      />
      <RemoveModal driver={removing} onClose={() => setRemoving(null)} onConfirm={handleRemove} loading={removeLoad} />
      {showRegister && <RegisterModal companies={companies} onClose={() => setShowRegister(false)} onSaved={d => { setDrivers(prev => [d, ...prev]); setShowRegister(false) }} />}
      {showCompany  && <CompanyModal  onClose={() => setShowCompany(false)}  onSaved={c => { setCompanies(prev => [c, ...prev]); setShowCompany(false) }} />}
      {editing && <EditDriverModal driver={editing} onClose={() => setEditing(null)} onSaved={handleDriverSaved} />}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}